import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  ViewChild,
} from '@angular/core';
import {
  LocationExternalAutocompleteComponent,
  TextFieldsComponent,
  CommonCommentsComponent,
  EmailValidatorComponent,
  PhoneValidatorComponent,
} from '@loup-transload-ui-app/common-components';
import { CommonModule, DatePipe } from '@angular/common';
import { VendorService } from '../../api/external-web/vendor.service';
import { SnackbarService } from '@loup-transload-ui-app/common-services';

@Component({
  selector: 'app-vendor-registration',
  templateUrl: './vendor-registration.component.html',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    LocationExternalAutocompleteComponent,
    TextFieldsComponent,
    CommonCommentsComponent,
    EmailValidatorComponent,
    PhoneValidatorComponent,
  ],
  providers: [DatePipe],
  styleUrl: './vendor-registration.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class VendorRegistrationComponent {
  private readonly vendorService = inject(VendorService);
  private readonly snackbar = inject(SnackbarService);
  @ViewChild('legalCompanyName') legalCompanyName: TextFieldsComponent;
  @ViewChild('businessNameDba') businessNameDba: TextFieldsComponent;
  @ViewChild('facilityAddress1') facilityAddress1: TextFieldsComponent;
  @ViewChild('facilityAddress2') facilityAddress2: TextFieldsComponent;
  @ViewChild('contactName') contactName: TextFieldsComponent;
  @ViewChild('email') email: EmailValidatorComponent;
  @ViewChild('phone') phone: PhoneValidatorComponent;
  @ViewChild('comment') comment: CommonCommentsComponent;
  @ViewChild('cityStateCountry')
  cityStateCountry: LocationExternalAutocompleteComponent;
  legalCompany = 'Legal Company Name';
  businessName = 'Bussiness Name (DBA)';
  facilityAddressOne = 'Facility Primary Address';
  facilityAddressTwo = 'Facility Additional Address';
  name = 'Contact Name';
  emailAddress = 'E-Mail';
  phoneNumber = 'Phone Number';
  isRequired = ' is required.';
  vendorRegistrationForm: FormGroup;
  isSubmitted = false;

  constructor(private readonly fb: FormBuilder) {
    this.vendorRegistrationForm = this.fb.group({
      legalCompanyName: ['', Validators.required],
      businessName: ['', Validators.required],
      facilityAddress1: ['', Validators.required],
      facilityAddress2: [''],
      cityStateCountry: ['', Validators.required],
      contactName: ['', Validators.required],
      phoneNumber: ['', Validators.required],
      email: ['', Validators.required],
      comment: [''],
    });
  }

  onClear() {
    this.isSubmitted = false;
    this.vendorRegistrationForm.reset();
    this.legalCompanyName?.textFieldForm.setValue('');
    this.legalCompanyName.errorMessageText = '';
    this.legalCompanyName.errorState = null;
    this.businessNameDba?.textFieldForm.setValue('');
    this.businessNameDba.errorMessageText = '';
    this.businessNameDba.errorState = null;
    this.facilityAddress1?.textFieldForm.setValue('');
    this.facilityAddress1.errorMessageText = '';
    this.facilityAddress1.errorState = null;
    this.facilityAddress2?.textFieldForm.setValue('');
    this.cityStateCountry?.locationControl.setValue('');
    this.cityStateCountry.locationAutoCompleteMessage = '';
    this.cityStateCountry.locationAutoCompleteState = null;
    this.cityStateCountry.inputStringValue = '';
    this.contactName?.textFieldForm.setValue('');
    this.contactName.errorMessageText = '';
    this.contactName.errorState = null;
    this.email?.emailControl.setValue('');
    this.email.errorMessageText = '';
    this.email.errorState = null;
    this.phone?.phoneControl.setValue('');
    this.phone.errorMessageText = '';
    this.phone.errorState = null;
    this.comment?.commentsFormData.setValue('');
  }

  saveVendorDetails(): void {
    this.isSubmitted = true;
    if (!this.vendorRegistrationForm.valid) {
      return;
    }
    const formValues = this.vendorRegistrationForm.value;
    const requestObj = {
      legalName: formValues.legalCompanyName,
      dbaName: formValues.businessName,
      comment: formValues.comment,
      address: {
        addressLine1: formValues.facilityAddress1,
        addressLine2: formValues.facilityAddress2,
        cityStateCountry: {
          city: formValues.cityStateCountry?.city,
          state: formValues.cityStateCountry?.state,
          country: formValues.cityStateCountry?.country,
          latitude: formValues.cityStateCountry?.latitude,
          longitude: formValues.cityStateCountry?.longitude,        
        },
        postalCode: formValues.cityStateCountry?.postalCode
      },
      contact: {
        name: formValues.contactName,
        email: formValues.email,
        phone: formValues.phoneNumber,
      },
    };
    this.vendorService.createVendorRegistration(requestObj).subscribe({
      next: (res: any) => {
        if (res?.vendorRegistrationId) {
          const message = `Vendor #${res.vendorRegistrationId} has been successfully created.`;
          this.snackbar.show(message, 10000, 'bottom');
          this.onClear();
        }
      },
    });
  }

  fieldValue(event: any, fieldName: string) {
    this.vendorRegistrationForm.get(fieldName)?.setValue(event);
    this.vendorRegistrationForm.get(fieldName)?.markAsTouched();
  }
}
